import { useCallback } from 'react';
import { subYears } from 'date-fns';

import { DateInput } from '@ui/components/DateInput';
import { Form } from '@ui/components/Form';
import { FormInput, FormLabel, FormRow } from '@ui/components/Form/Form';
import { IconButton } from '@ui/components/IconButton';
import { Input } from '@ui/components/Input';
import { Tab, Tabs } from '@ui/components/Tabs';
import { RemoveIcon } from '@ui/icons/RemoveIcon';

import { Gender, RcfAttributes } from '@features/shared/api/typings';

import styles from './RcfAttributesForm.module.css';

const GENDER_TABS: Tab<Gender>[] = [
  { label: 'Мужской', value: 'Male' },
  { label: 'Женский', value: 'Female' },
];

type Props = {
  value: RcfAttributes;
  onChange: (newValue: RcfAttributes) => void;
};

const maxDate = new Date().toDateString();
const minDate = subYears(new Date(), 150).toDateString();

export function RcfAttributesForm({ value, onChange }: Props) {
  const onIdChange = useCallback(
    (rcfId: string) => {
      onChange({ ...value, rcfId });
    },
    [onChange, value],
  );
  const onBirthdayChange = useCallback(
    (birthdayDate: Date | null) => {
      onChange({
        ...value,
        birthdayTimestampMs: birthdayDate ? birthdayDate.getTime() : null,
      });
    },
    [onChange, value],
  );
  const onGenderChange = useCallback(
    (gender?: Gender) => {
      onChange({ ...value, gender });
    },
    [onChange, value],
  );

  const clearId = useCallback(() => {
    onIdChange('');
  }, [onIdChange]);
  const clearBirthday = useCallback(() => {
    onBirthdayChange(null);
  }, [onBirthdayChange]);
  const clearGender = useCallback(() => {
    onGenderChange(undefined);
  }, [onGenderChange]);

  return (
    <Form className={styles.form}>
      <FormRow>
        <FormLabel>ФШР&nbsp;ID</FormLabel>
        <FormInput className={styles.input}>
          <Input type="number" value={value.rcfId || ''} onChange={onIdChange} />
          <IconButton
            preset="transparent"
            size="28"
            className={styles.clearButton}
            onClick={clearId}
            disabled={!value.rcfId}
          >
            <RemoveIcon />
          </IconButton>
        </FormInput>
      </FormRow>

      <FormRow>
        <FormLabel>Дата рождения</FormLabel>
        <FormInput className={styles.input}>
          <DateInput
            value={value.birthdayTimestampMs}
            onChange={onBirthdayChange}
            min={minDate}
            max={maxDate}
          />
          <IconButton
            preset="transparent"
            size="28"
            className={styles.clearButton}
            onClick={clearBirthday}
            disabled={!value.birthdayTimestampMs}
          >
            <RemoveIcon />
          </IconButton>
        </FormInput>
      </FormRow>

      <FormRow>
        <FormLabel>Пол</FormLabel>
        <FormInput className={styles.input}>
          <Tabs flat tabs={GENDER_TABS} value={value.gender} onChange={onGenderChange} />
          <IconButton
            preset="transparent"
            size="28"
            className={styles.clearButton}
            onClick={clearGender}
            disabled={!value.gender}
          >
            <RemoveIcon />
          </IconButton>
        </FormInput>
      </FormRow>
    </Form>
  );
}
